import { useEffect, useState } from "react";

type UseTypingPlaceholderOptions = {
  /** Delay between typed characters (ms). */
  typingMs?: number;
  /** Delay between deleted characters (ms). */
  deletingMs?: number;
  /** Pause after a full phrase before deleting (ms). */
  holdMs?: number;
  /** Pause after delete before typing again (ms). */
  restartMs?: number;
  /** When false, type once and hold. Default true. */
  loop?: boolean;
  /** Skip animation (e.g. reduced motion). */
  disabled?: boolean;
};

function shouldPauseTypingAnimation(): boolean {
  if (typeof window === "undefined") return true;
  // Lighthouse / automated browsers: continuous timers destroy Performance score.
  if (navigator.webdriver) return true;
  if (document.visibilityState === "hidden") return true;
  return false;
}

/**
 * Character-by-character placeholder text. Loops type → hold → delete by default.
 * Pauses under reduced-motion callers, hidden tabs, and automation (Lighthouse).
 */
export function useTypingPlaceholder(
  text: string,
  {
    typingMs = 42,
    deletingMs = 28,
    holdMs = 2200,
    restartMs = 500,
    loop = true,
    disabled = false,
  }: UseTypingPlaceholderOptions = {},
) {
  const [displayed, setDisplayed] = useState(disabled || shouldPauseTypingAnimation() ? text : "");
  const [showCursor, setShowCursor] = useState(true);
  const [paused, setPaused] = useState(() => shouldPauseTypingAnimation());

  useEffect(() => {
    const syncPause = () => setPaused(shouldPauseTypingAnimation());
    syncPause();
    document.addEventListener("visibilitychange", syncPause);
    return () => document.removeEventListener("visibilitychange", syncPause);
  }, []);

  const inactive = disabled || paused;

  useEffect(() => {
    if (inactive || !text) {
      setDisplayed(text);
      return;
    }

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let index = 0;
    let deleting = false;

    const schedule = (fn: () => void, ms: number) => {
      timer = setTimeout(fn, ms);
    };

    const tick = () => {
      if (cancelled) return;

      if (!deleting) {
        index = Math.min(index + 1, text.length);
        setDisplayed(text.slice(0, index));
        if (index >= text.length) {
          if (!loop) return;
          deleting = true;
          schedule(tick, holdMs);
          return;
        }
        schedule(tick, typingMs);
        return;
      }

      index = Math.max(index - 1, 0);
      setDisplayed(text.slice(0, index));
      if (index <= 0) {
        deleting = false;
        schedule(tick, restartMs);
        return;
      }
      schedule(tick, deletingMs);
    };

    setDisplayed("");
    schedule(tick, typingMs);

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [text, typingMs, deletingMs, holdMs, restartMs, loop, inactive]);

  useEffect(() => {
    if (inactive || !loop) {
      setShowCursor(false);
      return;
    }
    const id = setInterval(() => setShowCursor((v) => !v), 530);
    return () => clearInterval(id);
  }, [inactive, loop]);

  if (inactive) return text;
  if (!loop && displayed === text) return text;
  return showCursor ? `${displayed}|` : `${displayed}\u00A0`;
}
